import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import store from '../../../store/store';
import { BaseComponent } from '../../base-component/base-component';
import { fetchSchools } from '../../user-profile/thunks/fetch-schools.thunk';
import { StoreState } from '../../../store/store.interface';
import { ActionStatus } from '../../../common/constants/action-status';
import { getSchoolResponse, getSchoolStatus } from '../../user-profile/user-profile.selectors';
import { getLtrListUrl } from '../utils/ltr.utils';
import { getQueryParam } from '../../../common/utils/util';
import { nothing } from 'lit-html';
import variables from '../../../common/styles/variables';
import emit from '../../../common/events';

/**
 * Component to display a list of schools for Learning Technology Representatives (LTR).
 * The schools are fetched based on a zip code query parameter.
 */
@customElement('mhecomm-ltr-school-list')
export class LearningTechnologyRepresentativeSchoolList extends connect(store)(BaseComponent) {

  static styles = [
    variables,
    css`
      .card {
        cursor: pointer;
      }

      .card:hover {
        background-color: var(--bs-light-blue) !important;
      }

      .card:focus {
        background-color: var(--bs-light-blue) !important;
        outline: 2px solid var(--mh-black) !important;
        box-shadow: none;
        outline-offset: 2px;
      }
    `,
  ];

  @state()
  schools: any[] = [];

  protected firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(_changedProperties);
    const zipCode = getQueryParam('zipcode') ?? '';
    if (zipCode) {
      store.dispatch(fetchSchools({countryCode: "US", stateCode: "", zipCode: zipCode, searchText: "", segment: "HE"}));
    }
  }

  stateChanged(state: StoreState) {
    if (getSchoolStatus(state) === ActionStatus.succeeded) {
      this.schools = getSchoolResponse(state).content;
    }
  }

  renderComp() {
    return html`
      <mhecomm-ltr-search-results
        .hasResults="${this.schools.length > 0}">
      </mhecomm-ltr-search-results>

      ${this.schools.length > 0 ? html`
        <div class="my-2">
          <h2>Choose Your School</h2>
          <span>Click on a result below to see representatives.</span>
        </div>
      ` : nothing}
      <div class="row row-cols-1 row-cols-md-4">
        ${this.schools.map(school => html`
          <div class="col my-2">
            <div class="card rounded bg-white shadow h-100"
                 tabindex="0"
                 @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this.goToLtrListUrl(school)}
                 @click="${() => this.goToLtrListUrl(school)}">
              <div class="card-body">
                <div class="fw-bold text-capitalize mb-1">${school.name.toLowerCase()}</div>
                <div class="text-capitalize mb-1">${school.address?.line1?.toLowerCase()}</div>
                <div class="text-capitalize mb-1">${school.address?.city?.toLowerCase()}</div>
              </div>
            </div>
          </div>
        `)}
      </div>
      ${this.schools.length > 0 ? html`
        <div class="row m-0 mt-4 mb-2">
          <hr aria-hidden="true"/>
        </div>
      ` : nothing}
    `;
  }

  private goToLtrListUrl(school: any) {
    this.emitClickEvent(school);
    window.location.href = getLtrListUrl(school.xid);
  }

  private emitClickEvent(school: any) {
    emit({
      element: this,
      name: 'ltr-choose-school-click',
      detail: {
        eventName: 'ltr-choose-school-click',
        schoolName: school.name,
        schoolId: school.xid
      }
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-ltr-school-list': LearningTechnologyRepresentativeSchoolList;
  }
}
